import type { ReactNode } from 'react';
import { merge } from 'lodash-es';
import React from 'react';

import type { VariantProp } from '../../common/colors';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { InternalProp } from '../../utilities/useInternal';
import type { AlertProps } from '../Alert/Alert';
import { getVariantStyles, variantBodyColor, variantHeadingColor } from '../../common/variant';
import { CopyBox } from '../../formatting/CopyBox/CopyBox';
import FeatureBadge from '../../formatting/FeatureBadge/FeatureBadge';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Body, BodySansSizes } from '../../text/Body';
import { Small, SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { spacingCSS } from '../../utilities/shared/Spacing';
import { widthCSS } from '../../utilities/shared/Width';
import { toLowerCaseFirstCharacterSmartIsh } from '../../utilities/strings';
import {
  type AccessLevelProp,
  AccessLevel,
  isAccessLevelVisible,
  UserAccessLevelContextProvider,
} from '../../utilities/useAccessLevelContext';
import { useAccessLevelOrInternal } from '../../utilities/useInternal';
import { Alert } from '../Alert/Alert';
import { LabelTooltip } from '../Tooltip/Tooltip';

type SummaryListSize = 'x-small' | 'medium';

export type SummaryListPairsProps = {
  /**
   * The label for the key.
   */
  label: React.ReactNode;
  /**
   * The width of the label.
   */
  labelWidth?: SpacingScale;
  /**
   * The value for the key.
   */
  value: React.ReactNode;
  /**
   * Access level for the pair.
   */
  accessLevel?: AccessLevelProp | null;
  /**
   * Pass in an alert to display below contents of the row.
   */
  alert?: Omit<AlertProps, 'type'>;
  /**
   * Boolean to show internal badge.
   */
  internal?: InternalProp;
  /**
   * Group identifier for organizing pairs.
   */
  group?: string;
  /**
   * Boolean to allow the value to be copied to clipboard.
   */
  copyable?:
    | boolean
    | {
        label: React.ReactNode;
        value?: string;
        rawValue?: string;
      };
  /**
   * Set the font family of the summary list.
   */
  monospaced?: boolean;
  /**
   * Provide a tooltip for the key.
   */
  tooltip?: React.ReactNode;
  /**
   * Set if the row has a special variant like `attention` or `negative` to highlight an issue.
   */
  variant?: VariantProp;
};

export type SummaryListGroupData = {
  /**
   * The label for the group.
   */
  label: React.ReactNode;
  /**
   * The pairs in this group.
   */
  pairs: SummaryListPairsProps[];
  /**
   * Boolean to show internal badge for the group.
   */
  internal?: boolean;
  /**
   * Set the size of the summary list for this group.
   */
  size?: SummaryListSize;
};

const summaryListContainerName = 'summary-list';
const summaryListContainers = {
  narrow: `@container ${summaryListContainerName} (max-width: 319px)`,
};

const SummaryListKeyLabelSmall = styled(Small, {
  color: '$$keyColor',
  fontWeight: '$bold',
});
SummaryListKeyLabelSmall.displayName = 'SummaryListKeyLabelSmall';

const SummaryListKeyLabel = styled(Text, {
  color: '$$keyColor',
  fontWeight: '$bold',
});
SummaryListKeyLabel.displayName = 'SummaryListKeyLabel';

const SummaryListKeyContainer = styled('div', {
  display: 'flex',
});
SummaryListKeyContainer.displayName = 'SummaryListKeyContainer';

const SummaryListValueContainer = styled(Text, {
  display: 'flex',
  width: '100%',
  maxWidth: '$700',
  wordBreak: 'break-word',
  color: '$$valueColor',

  variants: {
    monospaced: {
      true: {
        fontFamily: '$mono',
      },
      false: {
        fontFamily: '$sans',
      },
    },
    size: {
      'x-small': {
        ...SmallSansSizes,
      },
      medium: {
        ...BodySansSizes,
      },
    },
  },
});
SummaryListValueContainer.displayName = 'SummaryListValueContainer';

const SummaryListRowContainer = styled(AlignStack, {
  ...getVariantStyles('neutral'),
  width: '100%',
  $$keyColor: variantBodyColor,
  $$valueColor: variantHeadingColor,

  // In narrow containers, collapse from row to column. The doubled selector (&&) gives this
  // two-class specificity (0,2,0) vs AlignStack's single-class direction variant (0,1,0), so
  // it wins regardless of stylesheet injection order.
  [summaryListContainers.narrow]: {
    '&&': {
      flexDirection: 'column',
      gap: '$2',
    },
  },

  variants: {
    variant: {
      alternative: {
        ...getVariantStyles('alternative'),
      },
      attention: {
        ...getVariantStyles('attention'),
      },
      brand: {
        ...getVariantStyles('brand'),
      },
      negative: {
        ...getVariantStyles('negative'),
      },
      neutral: {
        ...getVariantStyles('neutral'),
      },
      positive: {
        ...getVariantStyles('positive'),
      },
    },
    internal: {
      true: {
        ...getVariantStyles('neutral', true),
      },
      false: {},
    },
  },
});
SummaryListRowContainer.displayName = 'SummaryListRowContainer';

const SummaryListRowWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$4',
});
SummaryListRowWrapper.displayName = 'SummaryListRowWrapper';

const getCopyable = (pair: SummaryListPairsProps) =>
  typeof pair.copyable === 'object'
    ? {
        label:
          pair.copyable?.label ||
          `Copy ${pair.label && toLowerCaseFirstCharacterSmartIsh(pair.label?.toString())}`,
        value: pair.copyable?.value || pair.value?.toString(),
        rawValue: pair.copyable?.rawValue,
      }
    : {
        label: `Copy ${pair.label && toLowerCaseFirstCharacterSmartIsh(pair.label?.toString())}`,
        value: pair.value?.toString(),
      };

function SummaryListPairs({
  accessLevel: accessLevelProp,
  internal,
  pairs,
  size,
  direction = 'row',
}: {
  accessLevel?: AccessLevelProp;
  internal?: InternalProp;
  pairs: SummaryListPairsProps[];
  size: SummaryListSize;
  direction?: 'column' | 'row';
}) {
  const accessLevel = useAccessLevelOrInternal({ accessLevel: accessLevelProp, internal });

  if (pairs) {
    return pairs
      .filter((pair) => pair.label != null && pair.value != null && pair.accessLevel !== null)
      .map((pair, index) => {
        const key = index;
        const labelWidthCSS =
          direction === 'row'
            ? merge(widthCSS(pair.labelWidth ?? 152), {
                [summaryListContainers.narrow]: { width: 'auto' },
              })
            : undefined;

        // Calculate access level for this pair
        let pairAccessLevel: AccessLevel | undefined;
        if (pair.accessLevel) {
          pairAccessLevel = pair.accessLevel as AccessLevel;
        } else if (pair.internal) {
          pairAccessLevel = AccessLevel.Internal;
        } else if (accessLevel) {
          pairAccessLevel = accessLevel as AccessLevel;
        }

        const KeyLabelTag = direction === 'row' ? SummaryListKeyLabelSmall : SummaryListKeyLabel;

        const keyLabel = <KeyLabelTag>{pair.label}</KeyLabelTag>;
        const keyElement = (
          <SummaryListKeyContainer css={labelWidthCSS}>
            <AlignStack
              align="center"
              gap={4}
              preset="small"
              width="fit-content"
              end={
                pairAccessLevel &&
                isAccessLevelVisible(pairAccessLevel) && (
                  <FeatureBadge arrangement="icon-only" type={pairAccessLevel} size="x-small" />
                )
              }
            >
              {pair.tooltip ? (
                <LabelTooltip contents={pair.tooltip} preset="small" truncate={false}>
                  {keyLabel}
                </LabelTooltip>
              ) : (
                keyLabel
              )}
            </AlignStack>
          </SummaryListKeyContainer>
        );

        // Render value with copyable functionality
        const copyableData = pair.copyable && getCopyable(pair);
        let valueElement: ReactNode;
        if (pair.value != null) {
          if (copyableData) {
            valueElement = (
              <CopyBox
                aria-label={copyableData.label}
                value={
                  copyableData.rawValue ??
                  copyableData.value ??
                  (pair.value != null ? pair.value : undefined)
                }
              >
                {pair.value}
              </CopyBox>
            );
          } else {
            valueElement = pair.value;
          }
        } else {
          valueElement = <>&mdash;</>;
        }

        return (
          <UserAccessLevelContextProvider key={`summaryListEcho-${key}`} value={pairAccessLevel}>
            <SummaryListRowWrapper>
              <SummaryListRowContainer
                align="start"
                direction={direction}
                gap={direction === 'row' ? 8 : 2}
                preset="body"
                start={keyElement}
                variant={pair.variant}
                internal={pairAccessLevel === AccessLevel.Internal}
              >
                <SummaryListValueContainer size={size} monospaced={pair.monospaced}>
                  {valueElement}
                </SummaryListValueContainer>
              </SummaryListRowContainer>
              {pair.alert ? <Alert {...pair.alert} type="inline" /> : null}
            </SummaryListRowWrapper>
          </UserAccessLevelContextProvider>
        );
      });
  }

  return null;
}

export function createSummaryListGroups(
  pairs: SummaryListPairsProps[],
  groupLabels: Record<string, React.ReactNode> = {},
): SummaryListGroupData[] {
  const groupsMap = new Map<string, SummaryListPairsProps[]>();

  // Group pairs by their group identifier
  pairs.forEach((pair) => {
    const groupKey = pair.group || 'default';
    if (!groupsMap.has(groupKey)) {
      groupsMap.set(groupKey, []);
    }
    groupsMap.get(groupKey)!.push(pair);
  });

  // Convert to SummaryListGroupData format
  return Array.from(groupsMap.entries()).map(([groupKey, groupPairs]) => ({
    label: groupLabels[groupKey] || groupKey,
    pairs: groupPairs,
  }));
}

const SummaryListGroupLabel = styled(Body, {
  display: 'flex',
  alignItems: 'center',
  gap: '$12',
  fontWeight: '$bold',

  '&::after': {
    content: '',
    flex: 1,
    height: '$1',
    backgroundColor: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      backgroundColor: colors.strokeNeutralDark,
    },
  },
});
SummaryListGroupLabel.displayName = 'SummaryListGroupLabel';

const SummaryListGroupItems = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});
SummaryListGroupItems.displayName = 'SummaryListGroupItems';

const SummaryListGroupContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$8',
  width: '100%',
});
SummaryListGroupContainer.displayName = 'SummaryListGroupContainer';

export type SummaryListGroupProps = {
  /**
   * Pass in any content as `children`.
   */
  children?: React.ReactNode;
  /**
   * Set the direction of the items in the list (only works if `pairs` is provided).
   */
  direction?: 'column' | 'row';
  /**
   * Set the gap between the children.
   */
  gap?: SpacingScale;
  /**
   * Boolean to show internal badge.
   */
  internal?: boolean;
  /**
   * Provide a label for the group with `label`.
   */
  label?: React.ReactNode;
  /**
   * Can pass in label-value pairs for the rows.
   */
  pairs?: SummaryListPairsProps[];
  /**
   * Set the size of the summary list.
   */
  size?: SummaryListSize;
};

export function SummaryListGroup({
  children,
  direction,
  gap,
  internal,
  label,
  pairs,
  size = 'medium',
  ...remaining
}: SummaryListGroupProps) {
  const css = merge(spacingCSS(gap));

  return (
    <SummaryListGroupContainer {...remaining}>
      <SummaryListGroupLabel>{label}</SummaryListGroupLabel>
      <SummaryListGroupItems css={css}>
        {children}
        {pairs && (
          <SummaryListPairs direction={direction} internal={internal} pairs={pairs} size={size} />
        )}
      </SummaryListGroupItems>
    </SummaryListGroupContainer>
  );
}

function SummaryListGroups({
  direction,
  gap,
  groups,
  size,
}: {
  direction?: 'column' | 'row';
  gap?: SpacingScale;
  groups: SummaryListGroupData[];
  size: SummaryListSize;
}) {
  if (groups) {
    return groups.map((group) => (
      <SummaryListGroup
        key={`summaryListEchoGroup-${group.label}`}
        direction={direction}
        gap={gap}
        internal={group.internal}
        label={group.label}
        pairs={group.pairs}
        size={size}
      />
    ));
  }

  return null;
}

export const SummaryListContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  containerType: 'inline-size',
  containerName: summaryListContainerName,
});
SummaryListContainer.displayName = 'SummaryListContainer';

export type SummaryListProps = {
  /**
   * Set the direction of the items in the list (only works if `pairs` is provided).
   */
  direction?: 'column' | 'row';
  /**
   * Set the gap between the children.
   */
  gap?: SpacingScale;
  /**
   * Access level of the summary list.
   */
  accessLevel?: AccessLevelProp | null;
  /**
   * Boolean to show internal badge.
   */
  internal?: InternalProp;
  /**
   * Can pass in label-value pairs for the rows.
   */
  pairs?: SummaryListPairsProps[];
  /**
   * Can pass in groups of pairs for organized display.
   */
  groups?: SummaryListGroupData[];
  /**
   * Set the size of the summary list.
   */
  size?: SummaryListSize;
};

export function SummaryList({
  accessLevel: accessLevelProp,
  direction = 'row',
  internal,
  gap,
  pairs,
  groups,
  size = 'medium',
  ...remaining
}: SummaryListProps) {
  const accessLevel = useAccessLevelOrInternal({ accessLevel: accessLevelProp, internal });

  const hasGroups = groups && groups.length > 0;
  const hasPairs = pairs && pairs.length > 0;

  if (!hasGroups && !hasPairs) {
    return null;
  }

  let groupsToRender = groups;
  let willHaveGroups = hasGroups;
  if (hasPairs && !hasGroups) {
    const pairsWithGroups = pairs!.filter((pair) => pair.group);
    if (pairsWithGroups.length > 0) {
      groupsToRender = createSummaryListGroups(pairs!);
      willHaveGroups = true;
    }
  }

  const finalGap = gap ?? (direction === 'row' ? 8 : 12);
  const css = spacingCSS(willHaveGroups ? 12 : finalGap);

  return (
    <UserAccessLevelContextProvider value={accessLevel}>
      <SummaryListContainer css={css} {...remaining}>
        {groupsToRender ? (
          <SummaryListGroups
            direction={direction}
            gap={finalGap}
            groups={groupsToRender}
            size={size}
          />
        ) : (
          <SummaryListPairs direction={direction} internal={internal} pairs={pairs!} size={size} />
        )}
      </SummaryListContainer>
    </UserAccessLevelContextProvider>
  );
}
